import React from 'react';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Sparkles } from 'lucide-react';
import { EditorContent } from '@tiptap/react';
import Select from 'react-select';
import type { Editor } from '@tiptap/react';
import type { DealFormData, Service } from '../../../lib/types';

const USE_SERVICE_AS_TITLE_KEY = 'deal_use_service_as_title';

interface DealStep1OfferDetailsPanelProps {
  isDark: boolean;
  language: string;
  t: any;
  useServiceAsTitle: boolean;
  setUseServiceAsTitle: (v: boolean) => void;
  services: Service[];
  formData: DealFormData;
  setFormData: React.Dispatch<React.SetStateAction<DealFormData>>;
  editor: Editor | null;
  selectStyles: any;
  handleServiceSelection: (serviceId: string) => void;
  onOpenAIAssistant: () => void;
  /** Variante layout: desktop = col-span-2 con sfondo grid; mobile = singolo box senza sfondo. */
  variant?: 'desktop' | 'mobile';
  /** Suffisso unico per gli id degli input toggle, evita collisioni quando il pannello
   *  è renderizzato sia in desktop che mobile sullo stesso albero. */
  toggleIdSuffix?: string;
}

/**
 * Step 1 colonna destra: campo "Titolo Offerta" (con toggle "da servizi")
 * + editor TipTap della descrizione + bottone "Genera introduzione IA".
 *
 * Estratto da DealForm per ridurre la dimensione del file principale.
 */
export function DealStep1OfferDetailsPanel({
  isDark,
  language,
  t,
  useServiceAsTitle,
  setUseServiceAsTitle,
  services,
  formData,
  setFormData,
  editor,
  selectStyles,
  handleServiceSelection,
  onOpenAIAssistant,
  variant = 'desktop',
  toggleIdSuffix = 'desktop',
}: DealStep1OfferDetailsPanelProps) {
  const handleToggleUseService = (checked: boolean) => {
    setUseServiceAsTitle(checked);
    localStorage.setItem(USE_SERVICE_AS_TITLE_KEY, JSON.stringify(checked));
    if (!checked) {
      setFormData({ ...formData, title: '' });
    }
  };

  // Header Notion-like: label uppercase tracking-wide + toggle inline a destra.
  const titleHeader = (
    <div className="flex items-center justify-between mb-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-black/55 dark:text-white/55">
        {language === 'it' ? 'Titolo' : 'Title'}
      </span>
      <ToggleFromServices
        id={`use-service-title-${toggleIdSuffix}`}
        checked={useServiceAsTitle}
        onChange={handleToggleUseService}
        language={language}
      />
    </div>
  );

  const titleInput = useServiceAsTitle ? (
    <div className={isDark ? 'glass-select' : ''}>
      <Select
        options={services.map(service => ({
          value: service.id,
          label: service.title,
          description: service.short_description,
        }))}
        value={services.find(s => s.title === formData.title) ? {
          value: services.find(s => s.title === formData.title)?.id || '',
          label: formData.title,
        } : null}
        onChange={(selected: any) => {
          if (selected) {
            handleServiceSelection(selected.value);
          } else {
            setFormData({ ...formData, title: '', description: '' });
            if (editor) editor.commands.setContent('');
          }
        }}
        placeholder={language === 'it' ? 'Cerca e seleziona un servizio…' : 'Search and select a service…'}
        isClearable
        isSearchable
        classNamePrefix="react-select"
        formatOptionLabel={(option: any) => (
          <div className="py-0.5">
            <div className={`font-medium text-[14px] ${isDark ? 'text-white' : 'text-black'}`}>{option.label}</div>
            {option.description && (
              <div className={`text-[12px] mt-0.5 line-clamp-2 ${isDark ? 'text-white/55' : 'text-black/55'}`}>{option.description}</div>
            )}
          </div>
        )}
        styles={selectStyles}
      />
    </div>
  ) : (
    <input
      type="text"
      className="block w-full px-0 py-1 bg-transparent border-0 border-b border-transparent hover:border-black/[0.1] dark:hover:border-white/15 focus:border-black/30 dark:focus:border-white/40 text-[24px] font-semibold text-black dark:text-white placeholder-black/30 dark:placeholder-white/30 focus:outline-none focus:ring-0 transition-colors"
      value={formData.title || ''}
      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
      placeholder={language === 'it' ? 'Titolo offerta…' : 'Deal title…'}
    />
  );

  const descriptionSection = (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-medium uppercase tracking-wide text-black/55 dark:text-white/55">
          {language === 'it' ? 'Descrizione' : 'Description'}
        </span>
        <button
          type="button"
          onClick={onOpenAIAssistant}
          title={language === 'it' ? 'Genera introduzione con IA' : 'Generate intro with AI'}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
        >
          <Sparkles className="h-3 w-3" />
          {language === 'it' ? 'IA' : 'AI'}
        </button>
      </div>
      {/* Editor Notion-style: niente box visibile, toolbar inline minimale
          posizionata sopra il contenuto. Il body fluisce direttamente nella
          pagina come un blocco di testo, senza bordi o bg differenziati. */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center gap-0.5 -ml-1 mb-1 opacity-60 hover:opacity-100 transition-opacity">
          <ToolbarButton active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()} Icon={Bold} />
          <ToolbarButton active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()} Icon={Italic} />
          <ToolbarButton
            active={editor?.isActive('link')}
            onClick={() => { const url = window.prompt('Enter URL'); if (url) editor?.chain().focus().setLink({ href: url }).run(); }}
            Icon={LinkIcon}
          />
          <ToolbarButton active={editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()} Icon={List} />
          <ToolbarButton active={editor?.isActive('orderedList')} onClick={() => editor?.chain().focus().toggleOrderedList().run()} Icon={ListOrdered} />
        </div>
        <EditorContent
          editor={editor}
          className="flex-1 overflow-y-auto py-1 focus:outline-none text-[14px] leading-relaxed text-black dark:text-white prose prose-sm dark:prose-invert max-w-none [&_.ProseMirror]:min-h-[200px] [&_.ProseMirror]:focus:outline-none [&_.ProseMirror_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child]:before:text-black/30 [&_.ProseMirror_p.is-editor-empty:first-child]:before:float-left [&_.ProseMirror_p.is-editor-empty:first-child]:before:pointer-events-none"
        />
      </div>
    </div>
  );

  if (variant === 'desktop') {
    return (
      <div className="md:col-span-2 bg-gray-50 dark:bg-gray-900/60 p-4 h-full flex flex-col overflow-hidden">
        <div className="mb-3 flex-shrink-0">
          {titleHeader}
          {titleInput}
        </div>
        {descriptionSection}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        {titleHeader}
        {titleInput}
      </div>
      <div className="min-h-[300px] flex flex-col">{descriptionSection}</div>
    </div>
  );
}

interface ToolbarButtonProps {
  active?: boolean;
  onClick: () => void;
  Icon: React.ComponentType<{ className?: string }>;
}

function ToolbarButton({ active, onClick, Icon }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-1 rounded transition-colors ${
        active
          ? 'bg-black/[0.08] dark:bg-white/[0.1] text-black dark:text-white'
          : 'text-black/60 dark:text-white/60 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] hover:text-black dark:hover:text-white'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

interface ToggleFromServicesProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  language: string;
}

function ToggleFromServices({ id, checked, onChange, language }: ToggleFromServicesProps) {
  return (
    <label htmlFor={id} className="flex items-center gap-1.5 cursor-pointer group">
      <span className="text-[11px] text-black/45 dark:text-white/45 group-hover:text-black/60 dark:group-hover:text-white/60 transition-colors">
        {language === 'it' ? 'Da servizi' : 'From services'}
      </span>
      <div className="relative">
        <input
          id={id}
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div className={`w-7 h-3.5 rounded-full transition-colors ${checked ? 'bg-blue-500 dark:bg-blue-500' : 'bg-black/10 dark:bg-white/15'}`} />
        <div className={`absolute top-0.5 left-0.5 w-2.5 h-2.5 bg-white rounded-full shadow-sm transition-transform ${checked ? 'translate-x-3.5' : 'translate-x-0'}`} />
      </div>
    </label>
  );
}
